/**
 * API Service Layer
 *
 * All communication with the FastAPI backend happens through this file.
 * The frontend sends JSON requests and receives JSON responses.
 *
 * Security notes:
 * - Firebase ID token is sent as Bearer token in Authorization header
 * - No encryption keys are ever sent to or received from the backend
 * - File content is encrypted client-side BEFORE being sent to the backend
 */

/**
 * FastAPI base URL — always absolute (never relative paths like /auth/me on :3000).
 * Resolved on each request via getApiBaseUrl(): (1) window.__CDEAA_API_URL_OVERRIDE__
 * from public/index.html on local dev hosts, (2) REACT_APP_API_URL, (3) default :8000.
 */
let __CDEAA_API_BASE_SOURCE = 'default';

function resolveApiBaseUrl() {
  let base = null;
  if (typeof window !== 'undefined') {
    const ov = window.__CDEAA_API_URL_OVERRIDE__;
    if (ov != null && String(ov).trim() !== '') {
      const o = String(ov)
        .replace(/\r/g, '')
        .trim()
        .replace(/\/$/, '');
      if (/^https?:\/\//i.test(o)) {
        __CDEAA_API_BASE_SOURCE = 'runtime-override(index.html)';
        base = o;
      }
    }
  }
  if (base == null) {
    const raw = String(process.env.REACT_APP_API_URL ?? '')
      .replace(/\r/g, '')
      .trim()
      .replace(/\/$/, '');
    if (raw) {
      __CDEAA_API_BASE_SOURCE = 'REACT_APP_API_URL';
      base = raw;
    } else {
      __CDEAA_API_BASE_SOURCE = 'default';
      base = 'http://127.0.0.1:8000';
    }
  }
  // Dev: never call FastAPI paths on the CRA origin (fixes /auth/me → localhost:3000).
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    try {
      const p = window.location.protocol;
      if ((p === 'http:' || p === 'https:') && /^https?:\/\//i.test(String(base))) {
        if (new URL(base).origin === window.location.origin) {
          __CDEAA_API_BASE_SOURCE = 'dev-spa-origin-rewritten-to-backend';
          base = 'http://127.0.0.1:8000';
        }
      }
    } catch {
      /* keep base */
    }
  }
  return base;
}

/**
 * Current API base — resolved on every call so requests never use a stale :3000 URL
 * if `window.__CDEAA_API_URL_OVERRIDE__` appears after the first module evaluation.
 */
export function getApiBaseUrl() {
  return resolveApiBaseUrl();
}

/**
 * When the production build has no REACT_APP_API_URL and no runtime override,
 * the client defaults to http://127.0.0.1:8000 — which breaks hosted frontends (Vercel, etc.).
 * Returns a short admin-facing message, or null when the API base is explicitly configured.
 */
export function getApiConfigurationWarning() {
  if (typeof window === 'undefined') return null;
  if (process.env.NODE_ENV !== 'production') return null;
  try {
    const h = String(window.location.hostname || '');
    if (h === 'localhost' || h === '127.0.0.1') return null;
  } catch {
    return null;
  }
  resolveApiBaseUrl();
  if (__CDEAA_API_BASE_SOURCE !== 'default') return null;
  return (
    'Backend URL is not set for this deployment. In Vercel → Project → Settings → Environment Variables, ' +
    'add REACT_APP_API_URL with your API base URL (no trailing slash), e.g. https://your-api.onrender.com, ' +
    'then redeploy. Or set window.__CDEAA_API_URL_OVERRIDE__ in public/index.html before the app loads.'
  );
}

(function validateApiBaseUrl() {
  const base = getApiBaseUrl();
  try {
    const u = new URL(base);
    const host = u.hostname.toLowerCase();
    if (host === 'awsapprunner.com' || host === 'www.awsapprunner.com') {
      console.warn(
        '[CDEaaS] REACT_APP_API_URL must be your full API hostname (e.g. xxx.onrender.com), not bare awsapprunner.com'
      );
    }
  } catch {
    console.error('[CDEaaS] Invalid REACT_APP_API_URL — must be absolute, e.g. http://127.0.0.1:8000. Got:', base);
  }
  if (typeof window !== 'undefined') {
    // Both names documented for console checks (docs vary on trailing underscores).
    window.__CDEAA_API_BASE__ = base;
    window.__CDEAA_API_BASE = base;
    if (process.env.NODE_ENV === 'development') {
      console.info(
        '[CDEaaS] API requests go to:',
        base,
        '| source:',
        __CDEAA_API_BASE_SOURCE,
        '| build-time REACT_APP_API_URL:',
        JSON.stringify(process.env.REACT_APP_API_URL ?? '')
      );
      console.info('[CDEaaS] Console check:', 'window.__CDEAA_API_BASE__ or window.__CDEAA_API_BASE ===', JSON.stringify(base));
      const bad = typeof window.location.origin === 'string' && base.startsWith(window.location.origin);
      if (bad) {
        console.error(
          '[CDEaaS] REACT_APP_API_URL matches the React dev server origin — API calls would hit :3000 and return HTML. Set REACT_APP_API_URL=http://127.0.0.1:8000 and restart npm.'
        );
      }
    } else {
      try {
        const h = String(window.location.hostname || '');
        if (h && h !== 'localhost' && h !== '127.0.0.1' && __CDEAA_API_BASE_SOURCE === 'default') {
          console.error(
            '[CDEaaS] Production UI is calling',
            base,
            '— set REACT_APP_API_URL at build time (Vercel env) or window.__CDEAA_API_URL_OVERRIDE__ in index.html.'
          );
        }
      } catch {
        /* ignore */
      }
    }
  }
})();

if (process.env.NODE_ENV === 'development' && !/^https?:\/\//i.test(getApiBaseUrl())) {
  console.error(
    '[CDEaaS] REACT_APP_API_URL must start with http:// or https:// — relative URLs hit the React dev server and return HTML.'
  );
}

const TOKEN_KEY = 'firebaseToken';

/** Single key for session JWT — import this from pages to avoid typos vs localStorage. */
export const AUTH_TOKEN_STORAGE_KEY = TOKEN_KEY;

/** Compact token string (same rules as backend normalize_bearer_token). */
export function normalizeSessionToken(raw) {
  const s = raw == null ? '' : String(raw);
  return s.trim().split(/\s+/).join('');
}

/** Session JWT from localStorage. */
function getStoredAuthToken() {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? normalizeSessionToken(t) : '';
  } catch {
    return '';
  }
}

/** Merge caller headers then force Bearer last so Authorization is never dropped by spread order. */
function mergeAuthHeaders(extraHeaders, token) {
  const h = { ...(extraHeaders || {}) };
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function debugLogAuth(endpoint, token) {
  if (process.env.NODE_ENV !== 'development') return;
  const len = token ? token.length : 0;
  if (!len) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    console.warn(
      '[CDEaaS] API request without token:',
      endpoint,
      '| key:',
      TOKEN_KEY,
      '| app origin:',
      origin,
      '| Tip: login on http://localhost:3000 vs http://127.0.0.1:3000 uses different localStorage — pick one URL.'
    );
  } else {
    console.debug('[CDEaaS] API', endpoint, 'Bearer len=', len);
  }
}

/** Thin wrapper around fetch (same signature as the global). */
async function _fetchApi(url, init) {
  if (typeof window !== 'undefined') {
    const p = window.location.protocol;
    if (p !== 'http:' && p !== 'https:') {
      throw new Error('CDEaaS: API calls require an http(s) page context');
    }
  }
  try {
    return await fetch(url, init);
  } catch (err) {
    throw err;
  }
}

/**
 * Helper: Make an authenticated API request.
 * Automatically attaches the Firebase token from localStorage.
 */
async function apiRequest(endpoint, options = {}) {
  const token = getStoredAuthToken();
  debugLogAuth(endpoint, token);

  const headers = mergeAuthHeaders(options.headers, token);

  // Add JSON content type only when there's a body that isn't FormData
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await _fetchApi(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Only clear session if this request actually sent a token (avoid wiping on bugs/mis-ordered calls)
    if (token) {
      try {
        localStorage.removeItem(TOKEN_KEY);
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    const error = await response.json().catch(() => ({ detail: 'Unauthorized' }));
    throw new Error(error.detail || 'Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}


// ── Authentication ──────────────────────────────────────────────

/**
 * Get current authenticated user's profile.
 * Returns user object on success. On 401 (invalid/expired token), returns null
 * so the app can clear the token without throwing (avoids console noise on load).
 */
export async function getCurrentUser() {
  const token = getStoredAuthToken();
  if (!token) return null;

  debugLogAuth('/auth/me', token);
  const response = await _fetchApi(`${getApiBaseUrl()}/auth/me`, {
    headers: mergeAuthHeaders(null, token),
  });

  if (response.status === 401) {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Logout — calls backend to create LOGOUT audit event.
 * MUST be called BEFORE clearing the local token.
 * Security: LOGOUT must be recorded by the backend, not frontend only.
 */
export async function logoutUser() {
  return apiRequest('/auth/logout', { method: 'POST' });
}

/**
 * Login with email and password (step 1 of 2).
 *
 * Returns EITHER:
 *   { step: "otp_required", login_token, expires_in_minutes, message }
 *   — password correct; OTP emailed; call verifyLoginOTP() next
 *
 * Throws on failure (wrong password, suspended, etc.).
 */
export async function loginUser(email, password) {
  const response = await _fetchApi(`${getApiBaseUrl()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Login failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Verify login OTP (step 2 of 2).
 *
 * Called after loginUser() returns { step: "otp_required" }.
 * Returns { token, user } on success, throws on failure.
 */
export async function verifyLoginOTP(loginToken, otpCode) {
  const response = await _fetchApi(`${getApiBaseUrl()}/auth/verify-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login_token: loginToken, otp_code: otpCode }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Verification failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Register a new user with email, password, and display name.
 * Returns { token, user } on success, throws on failure.
 */
export async function registerUser(email, password, displayName) {
  const response = await _fetchApi(`${getApiBaseUrl()}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      password,
      display_name: displayName,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}


// ── Encrypted Files ─────────────────────────────────────────────

/**
 * Upload an encrypted file to the backend.
 *
 * IMPORTANT: The file must be encrypted BEFORE calling this function.
 * The backend never sees plaintext file content.
 *
 * @param {Blob} encryptedBlob - The encrypted file data
 * @param {string} originalFilename - The original filename (for metadata)
 * @param {string} iv - The initialization vector (hex string)
 * @param {string} fileHash - SHA-256 hash of the encrypted blob
 */
export async function uploadEncryptedFile(encryptedBlob, originalFilename, iv, fileHash, options = {}) {
  const token = getStoredAuthToken();
  debugLogAuth('/files/upload', token);
  if (!token) {
    throw new Error('Not authenticated. Sign in again.');
  }
  const formData = new FormData();
  formData.append('file', encryptedBlob, 'encrypted.bin');
  formData.append('original_filename', originalFilename);
  formData.append('iv', iv);
  formData.append('file_hash', fileHash);

  // Security level metadata
  if (options.securityLevel) formData.append('security_level', options.securityLevel);
  if (options.algorithm) formData.append('encryption_algorithm', options.algorithm);
  if (options.iterations) formData.append('pbkdf2_iterations', String(options.iterations));
  if (options.salt) formData.append('salt', options.salt);

  return apiRequest('/files/upload', {
    method: 'POST',
    body: formData,
  });
}

/**
 * List all encrypted files for the current user.
 * Returns metadata only, never file content.
 */
export async function listEncryptedFiles() {
  return apiRequest('/files/list');
}

/**
 * Get metadata for a specific encrypted file.
 */
export async function getFileMetadata(fileId) {
  return apiRequest(`/files/${fileId}`);
}

/**
 * Delete an encrypted file.
 * Normal user (owner) delete: no OTP required. Admin: OTP required.
 */
export async function deleteEncryptedFile(fileId) {
  return apiRequest(`/files/${fileId}`, { method: 'DELETE' });
}

/**
 * Cancel the current user's own scheduled file deletion. No OTP required (owner only).
 */
export async function cancelOwnScheduledDelete(fileId) {
  return apiRequest(`/files/${fileId}/cancel-scheduled-delete`, { method: 'POST' });
}

/**
 * Download an encrypted file blob from the backend.
 * Returns the raw encrypted ArrayBuffer (NOT decrypted).
 * Client must decrypt this locally using the encryption password.
 */
export async function downloadEncryptedFile(fileId) {
  const token = getStoredAuthToken();
  debugLogAuth(`/files/${fileId}/download`, token);
  if (!token) {
    throw new Error('Not authenticated. Sign in again.');
  }
  const response = await _fetchApi(`${getApiBaseUrl()}/files/${fileId}/download`, {
    headers: mergeAuthHeaders(null, token),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to download encrypted file' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.arrayBuffer();
}


// ── OTP Verification ────────────────────────────────────────────

/**
 * Request an OTP for step-up auth.
 * @param {string} fileId - File ID (required for file-related actions)
 * @param {string} [actionType='decrypt'] - One of: decrypt, access_approve, schedule_delete, force_delete, file_delete
 */
export async function requestOTP(fileId, actionType = 'decrypt') {
  return apiRequest('/otp/request', {
    method: 'POST',
    body: JSON.stringify({ file_id: fileId, action_type: actionType }),
  });
}

/**
 * Verify an OTP code for step-up auth.
 * @param {string} fileId - File ID
 * @param {string} otpCode - 6-digit code
 * @param {string} [actionType='decrypt'] - Must match the action_type used in requestOTP
 */
export async function verifyOTP(fileId, otpCode, actionType = 'decrypt') {
  const code = typeof otpCode === 'string' ? otpCode.trim() : String(otpCode || '').trim();
  return apiRequest('/otp/verify', {
    method: 'POST',
    body: JSON.stringify({
      file_id: fileId,
      otp_code: code,
      action_type: actionType,
    }),
  });
}


// ── Email Inbox (Simulated) ──────────────────────────────────────

/**
 * Get emails from simulated inbox.
 */
export async function getEmails() {
  return apiRequest('/email/inbox');
}

/**
 * Get unread email count (for notification badge).
 */
export async function getUnreadCount() {
  return apiRequest('/email/unread-count');
}

/**
 * Mark an email as read.
 */
export async function markEmailRead(emailId) {
  return apiRequest(`/email/${emailId}/read`, { method: 'POST' });
}


// ── User Activity ───────────────────────────────────────────────

/**
 * Get recent security activity for current user.
 */
export async function getRecentActivity() {
  return apiRequest('/activity/recent');
}

/**
 * Get security stats for current user.
 */
export async function getUserStats() {
  return apiRequest('/activity/stats');
}

/**
 * Log successful client-side decryption (audit only; no plaintext sent).
 * @param {string} fileId
 * @param {'owner'|'shared'} [context='owner']
 */
export async function logClientDecryptComplete(fileId, context = 'owner') {
  return apiRequest('/activity/client-decrypt-complete', {
    method: 'POST',
    body: JSON.stringify({ file_id: fileId, context }),
  });
}


// ── Access Requests ──────────────────────────────────────────────

/**
 * List ALL encrypted files with public metadata (all users).
 * Returns metadata only — no content, keys, or hashes.
 */
export async function listAllFiles() {
  return apiRequest('/access/files');
}

/**
 * Request access to another user's file.
 */
export async function createAccessRequest(fileId, reason = '') {
  return apiRequest('/access/request', {
    method: 'POST',
    body: JSON.stringify({ file_id: fileId, reason }),
  });
}

/**
 * Get all access requests (incoming + outgoing) for current user.
 */
export async function getAccessRequests() {
  return apiRequest('/access/requests');
}

/**
 * Get count of pending access requests for current user's files.
 */
export async function getPendingAccessCount() {
  return apiRequest('/access/pending-count');
}

/**
 * Approve an access request (requires OTP).
 */
export async function approveAccessRequest(requestId, expiryHours = 24) {
  return apiRequest('/access/approve', {
    method: 'POST',
    body: JSON.stringify({
      request_id: requestId,
      expiry_hours: expiryHours,
    }),
  });
}

/**
 * Reject an access request.
 */
export async function rejectAccessRequest(requestId) {
  return apiRequest('/access/reject', {
    method: 'POST',
    body: JSON.stringify({ request_id: requestId }),
  });
}


/**
 * Download an encrypted file as an approved requester.
 * Returns the raw encrypted ArrayBuffer for client-side decryption.
 */
export async function downloadApprovedFile(fileId) {
  const token = getStoredAuthToken();
  debugLogAuth(`/access/download/${fileId}`, token);
  if (!token) {
    throw new Error('Not authenticated. Sign in again.');
  }
  const response = await _fetchApi(`${getApiBaseUrl()}/access/download/${fileId}`, {
    headers: mergeAuthHeaders(null, token),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Download failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.arrayBuffer();
}

/**
 * Get decryption metadata (salt, IV, iterations) for an approved shared file.
 * This allows the recipient to decrypt client-side if they know the password.
 */
export async function getApprovedFileMetadata(fileId) {
  return apiRequest(`/access/file-metadata/${fileId}`);
}


// ── Notifications ────────────────────────────────────────────────

/**
 * Get all notifications for the current user.
 */
export async function getNotifications() {
  return apiRequest('/notifications/list');
}

/**
 * Get unread notification count for badge display.
 */
export async function getUnreadNotificationCount() {
  return apiRequest('/notifications/unread-count');
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationRead(notificationId) {
  return apiRequest(`/notifications/mark-read/${notificationId}`, { method: 'POST' });
}

/**
 * Mark all notifications as read.
 */
export async function markAllNotificationsRead() {
  return apiRequest('/notifications/mark-all-read', { method: 'POST' });
}

/**
 * Delete a single notification.
 */
export async function deleteNotification(notificationId) {
  return apiRequest(`/notifications/${notificationId}`, { method: 'DELETE' });
}

/**
 * Clear (delete) all notifications for the current user.
 */
export async function clearAllNotifications() {
  return apiRequest('/notifications/clear-all', { method: 'POST' });
}


// ── Approved Files (for dashboard) ───────────────────────────────

/**
 * Get files where current user has approved, non-expired access.
 * Returns metadata + expiry_time for countdown display.
 */
export async function getMyApprovedFiles() {
  return apiRequest('/access/my-approved');
}


// ── Admin Endpoints ─────────────────────────────────────────────

/**
 * Get admin dashboard metrics.
 */
export async function getAdminDashboard() {
  return apiRequest('/admin/dashboard');
}

/**
 * Get runtime security policy thresholds used by backend OTP/alerts.
 */
export async function getAdminSecurityPolicy() {
  return apiRequest('/admin/security-policy');
}

/**
 * Update runtime security policy thresholds (admin only).
 */
export async function updateAdminSecurityPolicy(alertThreshold, autoLockThreshold) {
  return apiRequest('/admin/security-policy', {
    method: 'PUT',
    body: JSON.stringify({
      alert_threshold: Number(alertThreshold),
      auto_lock_threshold: Number(autoLockThreshold),
    }),
  });
}

/**
 * Get audit logs with optional filters.
 */
export async function getAuditLogs(eventType = null, userId = null, resource = null, startDate = null, endDate = null) {
  const params = new URLSearchParams();
  if (eventType) params.append('event_type', eventType);
  if (userId) params.append('user_id', userId);
  if (resource) params.append('resource', resource);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const queryString = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/admin/audit-logs${queryString}`);
}

/**
 * Export audit logs as CSV. Same filters as getAuditLogs.
 */
export async function exportAuditLogsCsv(eventType = null, userId = null, resource = null, startDate = null, endDate = null) {
  const params = new URLSearchParams();
  if (eventType) params.append('event_type', eventType);
  if (userId) params.append('user_id', userId);
  if (resource) params.append('resource', resource);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const token = getStoredAuthToken();
  debugLogAuth('/admin/audit-logs/export', token);
  if (!token) {
    throw new Error('Not authenticated. Sign in again.');
  }
  const res = await _fetchApi(`${getApiBaseUrl()}/admin/audit-logs/export${queryString}`, {
    headers: mergeAuthHeaders(null, token),
  });
  if (!res.ok) throw new Error('Export failed');
  return res.blob();
}

/**
 * Get security alerts (with lifecycle status).
 * Opening this endpoint marks NEW alerts as SEEN (badge clears).
 */
export async function getSecurityAlerts() {
  return apiRequest('/admin/security-alerts');
}

/**
 * Get count of NEW alerts for the notification badge.
 * Badge counts alerts with status=NEW only.
 */
export async function getNewAlertCount() {
  return apiRequest('/admin/alerts/new-count');
}

/**
 * Admin explicitly reviews an alert (SEEN → REVIEWED).
 */
export async function reviewAlert(alertId) {
  return apiRequest(`/admin/alerts/${alertId}/review`, { method: 'POST' });
}

/**
 * Admin resolves/closes an alert (REVIEWED → RESOLVED).
 */
export async function resolveAlert(alertId) {
  return apiRequest(`/admin/alerts/${alertId}/resolve`, { method: 'POST' });
}

/**
 * Get list of all users (admin only).
 * @param {boolean} [includeDeleted=false] - If true, include soft-deleted users in the list.
 */
export async function getUsers(includeDeleted = false) {
  const q = includeDeleted ? '?include_deleted=true' : '';
  return apiRequest(`/admin/users${q}`);
}

/**
 * Suspend a user. Suspended users cannot login.
 */
export async function suspendUser(userId) {
  return apiRequest(`/admin/users/${userId}/suspend`, { method: 'POST' });
}

/**
 * Activate a suspended user.
 */
export async function activateUser(userId) {
  return apiRequest(`/admin/users/${userId}/activate`, { method: 'POST' });
}

/**
 * Assign role (user or admin).
 */
export async function assignUserRole(userId, role) {
  return apiRequest(`/admin/users/${userId}/role`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });
}

/**
 * Soft-delete a user (admin only). User cannot login; audit trail preserved.
 */
export async function deleteUser(userId) {
  return apiRequest(`/admin/users/${userId}/delete`, { method: 'POST' });
}

/**
 * Restore a soft-deleted user (admin only).
 */
export async function restoreUser(userId) {
  return apiRequest(`/admin/users/${userId}/restore`, { method: 'POST' });
}

/**
 * Get all access requests (admin view).
 */
export async function getAdminAccessRequests(status = null) {
  const q = status ? `?status=${status}` : '';
  return apiRequest(`/admin/access-requests${q}`);
}

/**
 * Admin approve sharing request.
 */
export async function adminApproveAccess(requestId, expiryHours = 24) {
  return apiRequest(`/admin/access-requests/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ expiry_hours: expiryHours }),
  });
}

/**
 * Admin revoke access.
 */
export async function adminRevokeAccess(requestId) {
  return apiRequest(`/admin/access-requests/${requestId}/revoke`, { method: 'POST' });
}

/**
 * Admin extend access expiry.
 */
export async function adminExtendAccess(requestId, extensionHours = 24) {
  return apiRequest(`/admin/access-requests/${requestId}/extend`, {
    method: 'POST',
    body: JSON.stringify({ extension_hours: extensionHours }),
  });
}


// ── Admin File Management ────────────────────────────────────────

/**
 * Get list of all files with safe metadata only (admin view).
 * EXCLUDES: iv, salt, hash, storage_path — admin cannot decrypt.
 */
export async function getAdminFiles(search = '', sortBy = 'uploaded_at', order = 'desc', limit = 50, offset = 0) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (sortBy) params.append('sort_by', sortBy);
  if (order) params.append('order', order);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  return apiRequest(`/admin/files?${params.toString()}`);
}

/**
 * Get total file count for pagination.
 */
export async function getAdminFilesCount(search = '') {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  return apiRequest(`/admin/files/count${params}`);
}

/**
 * Schedule a file for deletion with grace period.
 */
export async function scheduleFileDelete(fileId, reason, gracePeriodDays = 2) {
  return apiRequest(`/admin/files/${fileId}/schedule-delete`, {
    method: 'POST',
    body: JSON.stringify({ reason, grace_period_days: gracePeriodDays }),
  });
}

/**
 * Cancel a scheduled file deletion.
 */
export async function cancelFileDelete(fileId) {
  return apiRequest(`/admin/files/${fileId}/cancel-delete`, { method: 'POST' });
}

/**
 * Force delete a file after grace period has expired.
 */
export async function forceDeleteFile(fileId) {
  return apiRequest(`/admin/files/${fileId}/force-delete`, { method: 'POST' });
}
